import { Immutable } from "immer";
import React from "react";
import { ErrorBoundary } from "tsi-common-react/src/components/ErrorBoundary/index";

import type { EmailSignupBlock as EmailSignupBlockValue } from "@reactivated";

// Client-only loader for the email-signup modal. The modal lives inside the
// reactivated document's React tree (rendered by this block) rather than being
// mounted by a separate createRoot/dynamicPlaceComponent scanner.
const EmailSignupModal = React.lazy(() =>
    import("../EmailSignupModal").then((m) => ({
        default: m.EmailSignupModal,
    })),
);

type IProps = Immutable<{ value: EmailSignupBlockValue }>;

/**
 * Email signup modal mount point.
 *
 * REWIRE of `home-email-subscription.html`, which gated the mount on a
 * server-side `{% if self.email_signup_status == 'active' %}` and emitted a
 * `<div data-place-react="email-signup-modal">`. The Python gate has moved to
 * block-level data (`value.email_signup_status`), so we apply it client-side
 * here and emit the same mount point when active.
 *
 * The modal renders as a child of the mount div, gated to client-only paint by
 * the `isHydrated` flag: `EmailSignupModal` reads `localStorage` in its
 * `useState` initializer (the "previously dismissed" gate), so a naive SSR
 * render would diverge between the server (no window -> renders the modal) and
 * first client hydration (localStorage says "closed" -> renders null), causing
 * a hydration mismatch and a flash of the modal. Because both the server render
 * and the first client render produce the bare mount div (`isHydrated` is false
 * until the post-hydration `useEffect` fires), there is no mismatch; the
 * localStorage read only ever runs client-side, after hydration. The modal is
 * lazy-imported (`React.lazy` + `Suspense`) so its chunk stays code-split and
 * loads only on the client, mirroring the old `await import()`. The
 * `ErrorBoundary` wraps `Suspense` so it catches both a lazy chunk-load failure
 * and any render error from the modal, scoping the blast radius to this block.
 */
export const EmailSignupBlock = (props: IProps): React.JSX.Element => {
    const [isHydrated, setIsHydrated] = React.useState(false);
    React.useEffect(() => setIsHydrated(true), []);
    if (props.value.email_signup_status !== "active") {
        return <></>;
    }
    return (
        <div data-place-react="email-signup-modal">
            {isHydrated && (
                <ErrorBoundary>
                    <React.Suspense fallback={null}>
                        <EmailSignupModal />
                    </React.Suspense>
                </ErrorBoundary>
            )}
        </div>
    );
};
